import { create } from "zustand";

/**
 * Auth Store - Memory Only (No Persistence)
 *
 * Security Architecture:
 * - Access Token: Stored in memory only (this store)
 * - Refresh Token: httpOnly cookie (managed by backend)
 *
 * On page refresh:
 * - Memory is cleared (access token lost)
 * - App calls /api/v1/auth/refresh on initialization
 * - Backend reads httpOnly cookie and returns new access token
 * - If cookie is invalid/expired, user must re-login
 *
 * Benefits:
 * - XSS Protection: Refresh token cannot be stolen via JavaScript
 * - No localStorage/sessionStorage: Nothing persists in browser storage
 * - Short-lived access: Access token only lives in memory
 */
export const useAuthStore = create((set, get) => ({
  // State
  user: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: true, // Start as loading until we verify auth status

  // Getters (for use outside React components)
  getAccessToken: () => get().accessToken,
  getUser: () => get().user,
  getIsAuthenticated: () => get().isAuthenticated,

  // Setters
  setUser: (user) =>
    set({
      user,
      isAuthenticated: !!user,
    }),

  setAccessToken: (accessToken) => set({ accessToken }),

  setLoading: (isLoading) => set({ isLoading }),

  /**
   * Set tokens after refresh
   * Only stores accessToken in memory (refreshToken is in httpOnly cookie)
   */
  setTokens: (accessToken) => {
    set({ accessToken });
  },

  /**
   * Login - Called after successful authentication
   * Access token stored in memory, refresh token in httpOnly cookie (set by backend)
   *
   * @param {Object} user - User object from backend
   * @param {string} accessToken - JWT access token (stored in memory only)
   */
  login: (user, accessToken) => {
    set({
      user,
      accessToken,
      isAuthenticated: true,
      isLoading: false,
    });
  },

  /**
   * Logout - Clears memory state
   * Backend clears the httpOnly cookie via /api/v1/auth/logout
   */
  logout: () => {
    set({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
    });
  },

  /**
   * Update user data (e.g., after profile update)
   */
  updateUser: (updates) =>
    set((state) => ({
      user: state.user ? { ...state.user, ...updates } : null,
    })),

  /**
   * Restore session from refresh token cookie
   * Called on app initialization by AuthProvider
   */
  restoreSession: (user, accessToken) => {
    set({
      user,
      accessToken,
      isAuthenticated: true,
      isLoading: false,
    });
  },

  /**
   * Mark initialization complete (no valid session found)
   */
  initComplete: () => {
    set({
      isLoading: false,
    });
  },
}));

export default useAuthStore;
